import React from 'react';
import { StyleSheet, View, Text, TouchableHighlight } from './base_components';
import TitleActionLine from './title_action_line';
import LinkHighlight from './buttons/link_highlight';
import Checkbox from './checkbox';

const styles = StyleSheet.create({
  box: {
    margin: '2rem',
    alignSelf: 'stretch',
    flexDirection: 'column',
    borderColor: 'var(--box-border-color)',
    borderWidth: 1,
    borderRadius: '1rem',
    overflow: 'hidden',
  },
  topLineView: {
    flexDirection: 'column',
    alignSelf: 'stretch',
    backgroundColor: 'var(--box-header-bg-color)',
    borderBottomWidth: 1,
    borderColor: 'var(--box-inner-line-color)',
  },
  topLine: {
    marginTop: '0.5rem',
    marginLeft: '2rem',
    marginRight: '2rem',
    marginBottom: '0.5rem',
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  title: {
    color: 'var(--box-title-text-color)',
    fontSize: '1.8em',
    fontWeight: 'bold',
  },
  headerLine: {
    height: '3rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    alignSelf: 'stretch',
    backgroundColor: 'var(--box-header-bg-color)',
    borderBottomWidth: 1,
    borderColor: 'var(--box-inner-line-color)',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  headerText: {
    marginLeft: '2rem',
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#ddd',
    color: '#222',
    fontSize: '1.4rem',
    fontWeight: 'bold',
    textDecoration: 'none',
  },
  headerCheckbox: {
    borderRightWidth: 1,
    borderRightColor: '#ddd',
  },
  last: {
    borderRightWidth: 0,
  },
  link: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  highlight: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  row: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  rowCells: {
    flex: 1,
    paddingTop: '1rem',
    paddingBottom: '1rem',
    minHeight: '2rem',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkbox: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderRightWidth: 1,
    borderRightColor: 'transparent',
  },
  cell: {
    marginLeft: '2rem',
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
  },
  cellText: {
    flex: 1,
    color: '#555',
    fontSize: '1.4rem',
    lineHeight: '2.5rem',
    fontWeight: '500',
    wordBreak: 'normal',
    overflowWrap: 'anywhere',
  },
  first: {
    color: 'var(--link-text-color)',
    textDecoration: 'underline',
  },
});

export default function StandardBoxList(props) {
  const {
    detail,
    headers,
    rows,
    sizes,
    selected,
    onChange,
    onPress,
    singleSelect,
    linkColumn,
  } = props;

  const show_checkboxes = Boolean(selected);
  const checkable_rows = rows?.reduce?.(
    (memo, i) => memo + (i.disable_checkbox ? 0 : 1),
    0
  );
  const all_checked =
    selected?.length === checkable_rows && selected?.length > 0;
  const has_rows = rows?.length > 0;

  function _onAllChange() {
    if (all_checked) {
      onChange?.([]);
    } else {
      const new_list = rows
        .map((row, i) => !row.disable_checkbox && i)
        .filter((i) => i !== false);
      onChange?.(new_list);
    }
  }
  function _onRowChange(i) {
    const new_list = singleSelect ? [] : selected.slice();
    const index = selected.indexOf(i);
    if (index === -1) {
      new_list.push(i);
    } else if (!singleSelect) {
      new_list.splice(index, 1);
    }
    onChange?.(new_list);
  }

  let header: any = null;
  if (headers) {
    header = (
      <View style={styles.headerLine}>
        {show_checkboxes ? (
          <Checkbox
            style={[styles.checkbox, styles.headerCheckbox]}
            disabled={!has_rows || singleSelect}
            value={all_checked && has_rows}
            onChange={_onAllChange}
          />
        ) : null}
        {headers.map((text, i) => (
          <Header
            key={i}
            text={text}
            size={sizes?.[i] ?? 1}
            last={i === headers.length - 1}
          />
        ))}
      </View>
    );
  }
  let content: any;
  if (rows?.length > 0) {
    content = rows.map((row, i) => {
      return (
        <Row
          key={i}
          showCheckboxes={show_checkboxes}
          value={selected?.indexOf?.(i) !== -1}
          index={i}
          sizes={sizes}
          onChange={_onRowChange}
          row={row}
          onPress={onPress}
          linkColumn={linkColumn}
        />
      );
    });
  }

  return (
    <View style={styles.box}>
      <View style={styles.topLineView}>
        <TitleActionLine
          style={styles.topLine}
          textStyle={styles.title}
          title={props.title}
          detail={detail !== undefined ? `(${detail})` : undefined}
        >
          {props.buttons}
        </TitleActionLine>
      </View>
      {header}
      {content}
      {props.children}
    </View>
  );
}
function Header(props) {
  return (
    <Text
      style={[
        styles.headerText,
        props.last ? styles.last : null,
        { flex: props.size },
      ]}
    >
      {props.text}
    </Text>
  );
}
function Row(props) {
  const { row, sizes, onPress, showCheckboxes, value, onChange } = props;
  const url = row?.url;
  const list = row?.list ?? row;
  const linkColumn = props.linkColumn ?? 0;
  const disable_checkbox = row?.disable_checkbox || false;

  const has_action = url || onPress;

  function _onPress() {
    onPress?.(row);
  }
  function _onChange() {
    onChange(props.index);
  }

  const body = (
    <View style={styles.rowCells}>
      {list.map((data, i) => (
        <Cell
          key={i}
          data={data}
          size={sizes?.[i] ?? 1}
          first={i === 0}
          last={i === row.length - 1}
          showAction={i === linkColumn && has_action}
        />
      ))}
    </View>
  );

  let content: any;
  if (url) {
    content = (
      <LinkHighlight
        style={styles.link}
        underlayColor='rgba(0,0,255,0.02)'
        url={url}
      >
        {body}
      </LinkHighlight>
    );
  } else if (onPress) {
    content = (
      <TouchableHighlight
        style={styles.highlight}
        underlayColor='rgba(0,0,255,0.02)'
        onPress={_onPress}
      >
        {body}
      </TouchableHighlight>
    );
  } else {
    content = <View style={styles.highlight}>{body}</View>;
  }
  return (
    <View style={styles.row}>
      {showCheckboxes ? (
        <Checkbox
          style={styles.checkbox}
          disabled={disable_checkbox}
          value={value}
          onChange={_onChange}
        />
      ) : null}
      {content}
    </View>
  );
}
function Cell(props) {
  const { data, last, showAction } = props;

  let content: any = null;
  if (typeof data !== 'object') {
    content = (
      <Text style={[styles.cellText, showAction ? styles.first : null]}>
        {data}
      </Text>
    );
  } else {
    content = data;
  }
  return (
    <View
      style={[styles.cell, last ? styles.last : null, { flex: props.size }]}
    >
      {content}
    </View>
  );
}
